import { SituacaoParecerRAM } from "@/@types/SituacaoParecerRAM";
import { ID_VETERINARIO_OFICIAL } from "@/constants/ID_VETERINARIO_OFICIAL";
import { IFormulario } from "@/interfaces/IFormulario";
import { IListaRelatorioAM } from "@/interfaces/IListaRelatorioAM";
import { IPlanoAcaoRelatorioAM } from "@/interfaces/IPlanoAcaoRelatorioAM";
import { IRelatorioAM } from "@/interfaces/IRelatorioAM";
import { api } from "@/services/api";
import { relatorioAMAction } from "@/store/actions/relatorioAM";
import { getRelatoriosAM } from "@/store/reducers/relatorioAM";
import { useAuth } from "react-oidc-context";
import { useDispatch, useSelector } from "react-redux";
import { useEstabelecimento } from "./useEstabelecimento";
import { useUsuarioLogado } from "./useUsuarioLogado";

type ClassificacaoForm = IFormulario["classificacaoFormulario"];

type TipoForm = IFormulario["tipoFormulario"];

export const useRelatorioAM = () => {
  const { user } = useAuth();
  const appDispatch = useDispatch();
  const { add, remove, update } = relatorioAMAction;
  const listaRelatorioAM = useSelector(getRelatoriosAM);
  const { usuarioLogado } = useUsuarioLogado();
  const { listaEstabelecimentos } = useEstabelecimento();

  const addRelatorioAM = (item: IRelatorioAM) => {
    appDispatch(add(item));
  };

  const removeRelatorioAM = (offlineId: number) => {
    appDispatch(remove(offlineId));
  };

  const updateRelatorioAM = (item: IRelatorioAM) => {
    appDispatch(update(item));
  };

  const getRelatorioAMByOfflineId = (offlineId: number) => {
    return listaRelatorioAM.find(
      (relatorio) => relatorio.offlineId === offlineId
    );
  };

  const submitRelatorioAMApi = async (formData: FormData) => {
    const { data } = await api.post<IRelatorioAM>(
      "/relatorios-monitoria-auditoria",
      formData,
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  };

  const cancelarRelatorioAMApi = async (id: number, motivo: string) => {
    await api.patch(
      `/relatorios-monitoria-auditoria/${id}/cancelar`,
      { motivo },
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
  };

  const getRelatorioAMByIdApi = async (id: number) => {
    const { data } = await api.get<IRelatorioAM>(
      `/relatorios-monitoria-auditoria/${id}`,
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
    return data;
  };

  const getRelatoriosAMApi = async (params: {
    page: number;
    estabelecimentoId: number;
    situacao?: string;
  }) => {
    const { data } = await api.get<IListaRelatorioAM>(
      `/relatorios-monitoria-auditoria`,
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
        params: {
          ...params,
          size: 20,
          page: params.page - 1,
        },
      }
    );
    return data;
  };

  const getAnexoApi = async (nomeUpload: string) => {
    try {
      const { data } = await api.get<Blob>(
        `/relatorios-monitoria-auditoria/anexo/${nomeUpload}`,
        {
          headers: { Authorization: `Bearer ${user?.access_token}` },
          responseType: "blob",
        }
      );
      return data;
    } catch (error) {
      const axiosError = error as any;
      if (error instanceof Error && "response" in error) {
        if (axiosError.response?.data instanceof Blob) {
          const text = await axiosError.response.data.text();
          try {
            const jsonError = JSON.parse(text);
            axiosError.response.data = jsonError;
          } catch {
            axiosError.response.data = { detail: text };
          }
        }
      }

      throw error;
    }
  };

  const updateRelatorioAMApi = async (formData: FormData, id: number) => {
    const { data } = await api.put<IRelatorioAM>(
      `/relatorios-monitoria-auditoria/${id}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  };

  const validateFinalizadoRelatorio = (relatorio: IRelatorioAM) => {
    // validacao se a fiscalizacao tem data, hora inicial, hora final e placa
    const fiscalizacoesInvalidas = (relatorio.fiscalizacoes || []).filter(
      (fiscalizacao) =>
        !fiscalizacao.data ||
        !fiscalizacao.horaInicial ||
        !fiscalizacao.horaFinal ||
        !fiscalizacao.placa
    );
    if (fiscalizacoesInvalidas.length > 0) {
      return false;
    }

    if (!relatorio.itens || relatorio.itens.length === 0) {
      return false;
    }

    // Validar se todas as perguntas obrigatórias foram respondidas
    const perguntasNaoRespondidas = relatorio.itens.filter(
      (itemRelatorio) => !itemRelatorio.alternativa?.id
    );

    if (perguntasNaoRespondidas.length > 0) {
      return false;
    }

    // Validar assinaturas
    if (!relatorio.assinaturas || relatorio.assinaturas.length === 0) {
      return false;
    }

    const assinaturaInvalida = relatorio.assinaturas.find(
      (assinante) =>
        assinante.statusAssinatura === "ASSINADO_DIGITALMENTE" &&
        assinante.assinatura === null
    );

    if (assinaturaInvalida) {
      return false;
    }

    // Validar campos obrigatórios
    if (!relatorio.conclusao?.trim()) {
      return false;
    }

    return true;
  };

  const submitPlanoAcaoRelatorioAMApi = async (
    relatorioId: number,
    formData: FormData
  ) => {
    const { data } = await api.post(
      `/relatorios-monitoria-auditoria/${relatorioId}/planos-acao`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return data;
  };

  const getAnexoPlanoAcaoApi = async (nomeUpload: string) => {
    try {
      const { data } = await api.get<Blob>(
        `/relatorios-monitoria-auditoria/anexo-plano-acao/${nomeUpload}`,
        {
          headers: { Authorization: `Bearer ${user?.access_token}` },
          responseType: "blob",
        }
      );
      return data;
    } catch (error) {
      const axiosError = error as any;
      if (error instanceof Error && "response" in error) {
        if (axiosError.response?.data instanceof Blob) {
          const text = await axiosError.response.data.text();
          try {
            const jsonError = JSON.parse(text);
            axiosError.response.data = jsonError;
          } catch {
            axiosError.response.data = { detail: text };
          }
        }
      }

      throw error;
    }
  };

  const getPlanoAcaoByItemIdApi = async (itemId: number) => {
    const { data } = await api.get<IPlanoAcaoRelatorioAM[]>(
      `/relatorios-monitoria-auditoria/${itemId}/planos-acao`,
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
    return data;
  };

  const submitParecerApi = async (
    planoAcaoId: number,
    content: { situacaoParecer: SituacaoParecerRAM; descricaoParecer: string }
  ) => {
    await api.patch(
      `/relatorios-monitoria-auditoria/plano-acao/${planoAcaoId}/parecer`,
      content,
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
  };

  const removerParecerPlanoAcaoApi = async (planoAcaoId: number) => {
    await api.patch(
      `/relatorios-monitoria-auditoria/plano-acao/${planoAcaoId}/parecer/remover`,
      {},
      {
        headers: { Authorization: `Bearer ${user?.access_token}` },
      }
    );
  };

  /** Verifica se o usuário pode fazer o plano de ação
   * - Para classificação INSPEÇÃO: pode fazer plano de ação se o usuário for inspetor do estabelecimento
   * - Para classificação INDUSTRIA: pode fazer plano de ação se o usuário for responsável pela industria
   * - Usuários com perfil administrador não podem fazer plano de ação, apenas visualizar
   */
  const canMakePlanoAcao = (
    classificacaoForm: ClassificacaoForm,
    estabelecimentoId: number
  ) => {
    const estabelecimento = listaEstabelecimentos.find(
      (estab) => estab.id === estabelecimentoId
    );

    if (!estabelecimento) {
      return false;
    }

    if (classificacaoForm === "INSPECAO") {
      const userIsInspetor = estabelecimento.pessoasInspecao.find(
        (pessoa) => pessoa.id === usuarioLogado.pessoaId && pessoa.inspetor
      );

      return Boolean(userIsInspetor);
    }

    if (classificacaoForm === "INDUSTRIA") {
      const userIsResponsavelIndustria = estabelecimento.pessoasInspecao.find(
        (pessoa) =>
          pessoa.id === usuarioLogado.pessoaId &&
          pessoa.funcao.id !== ID_VETERINARIO_OFICIAL &&
          pessoa.inspetor === false
      );

      return Boolean(userIsResponsavelIndustria);
    }

    return false;
  };

  /** Verifica se o usuário pode fazer o parecer
   * - tipo AUDITORIA: pode fazer parecer se o usuario for admin
   * - tipo INSPETOR: pode fazer parecer se o usuário for inspetor do estabelecimento
   * - tipo MONITORIA: pode fazer parecer se o usuário for veterinario oficial do estabelecimento
   */
  const canMakeParecer = (tipoForm: TipoForm, estabelecimentoId: number) => {
    const estabelecimento = listaEstabelecimentos.find(
      (estab) => estab.id === estabelecimentoId
    );

    if (!estabelecimento) {
      return false;
    }

    if (tipoForm === "AUDITORIA" && usuarioLogado.perfil.id === 1) {
      return true;
    }

    const pessoaInspecao = estabelecimento?.pessoasInspecao.find(
      (pi) => pi.id === usuarioLogado.pessoaId
    );

    if (!pessoaInspecao) {
      return false;
    }

    if (
      pessoaInspecao.funcao.id === ID_VETERINARIO_OFICIAL &&
      tipoForm === "MONITORIA"
    ) {
      return true;
    }

    if (pessoaInspecao.inspetor && tipoForm === "INSPETOR") {
      return true;
    }

    return false;
  };

  /**
   * Verifica se o usuário pode ver o plano de ação
   * - O plano de ação deve ser exibido para usuários com perfil administrador
   * - Para classificação INSPEÇÃO: o plano de ação deve ser exibido para usuários que forem inspetores do estabelecimento
   * - Para classificação INDUSTRIA: o plano de ação deve ser exibido para usuários que forem responsáveis indústria do estabelecimento
   * - O plano de ação deve ser exibido para usuários que forem veterinários e inspetores do estabelecimento, independente da classificação do formulário
   */
  const canViewPlanoAcao = (
    classificacaoForm: ClassificacaoForm,
    estabelecimentoId: number
  ) => {
    if (usuarioLogado.perfil.id === 1) {
      return true;
    }

    const estabelecimento = listaEstabelecimentos.find(
      (estab) => estab.id === estabelecimentoId
    );

    if (!estabelecimento) {
      return false;
    }

    const pessoaInspecao = estabelecimento?.pessoasInspecao.find(
      (pi) => pi.id === usuarioLogado.pessoaId
    );

    if (!pessoaInspecao) {
      return false;
    }

    const userIsVeterinario =
      pessoaInspecao.funcao.id === ID_VETERINARIO_OFICIAL;
    const userIsInspetor = pessoaInspecao.inspetor;
    const userIsResponsavelIndustria =
      pessoaInspecao.funcao.id !== ID_VETERINARIO_OFICIAL &&
      !pessoaInspecao.inspetor;

    if (userIsVeterinario || userIsInspetor) {
      return true;
    }

    if (classificacaoForm === "INDUSTRIA") {
      return userIsResponsavelIndustria;
    }

    return false;
  };

  /**
   * Verifica se o usuário pode ver e imprimir o relatório
   * Monitor, auditor e inspetor podem ver e imprimir o relatório
   * Responsável pela indústria pode ver e imprimir o relatório apenas para classificação INDUSTRIA
   */
  const canViewAndPrintRelatorio = (
    classificacaoForm: ClassificacaoForm,
    estabelecimentoId: number
  ) => {
    const isAdmin = usuarioLogado.perfil.id === 1;
    if (isAdmin) {
      return true;
    }

    const estabelecimento = listaEstabelecimentos.find(
      (estab) => estab.id === estabelecimentoId
    );
    if (!estabelecimento) {
      return false;
    }
    const pessoaInspecao = estabelecimento?.pessoasInspecao.find(
      (pi) => pi.id === usuarioLogado.pessoaId
    );

    if (!pessoaInspecao) {
      return false;
    }

    const userIsVeterinario =
      pessoaInspecao.funcao.id === ID_VETERINARIO_OFICIAL;
    const userIsInspetor = pessoaInspecao.inspetor;
    const userIsResponsavelIndustria =
      pessoaInspecao.funcao.id !== ID_VETERINARIO_OFICIAL &&
      !pessoaInspecao.inspetor;

    if (userIsVeterinario || userIsInspetor) {
      return true;
    }

    if (classificacaoForm === "INDUSTRIA" && userIsResponsavelIndustria) {
      return true;
    }

    return false;
  };

  /**
   * Verifica se o usuário pode cancelar o relatório
   * - O relatório pode ser cancelado por usuários com perfil administrador
   * - Para classificação INSPEÇÃO: o relatório pode ser cancelado por usuários que forem inspetores do estabelecimento
   * - Para classificação MONITORIA: o relatório pode ser cancelado por usuários que forem veterinários do estabelecimento
   * - Usuários responsáveis pela indústria não podem cancelar o relatório
   */
  const canCancelRelatorio = (
    tipoFormulario: TipoForm,
    estabelecimentoId: number
  ) => {
    const isAdmin = usuarioLogado.perfil.id === 1;
    if (isAdmin) {
      return true;
    }

    const estabelecimento = listaEstabelecimentos.find(
      (estab) => estab.id === estabelecimentoId
    );

    if (!estabelecimento) {
      return false;
    }

    const pessoaInspecao = estabelecimento?.pessoasInspecao.find(
      (pi) => pi.id === usuarioLogado.pessoaId
    );

    if (!pessoaInspecao) {
      return false;
    }

    const userIsVeterinario =
      pessoaInspecao.funcao.id === ID_VETERINARIO_OFICIAL;
    const userIsInspetor = pessoaInspecao.inspetor;

    if (tipoFormulario === "INSPETOR" && userIsInspetor) {
      return true;
    }

    if (tipoFormulario === "MONITORIA" && userIsVeterinario) {
      return true;
    }

    return false;
  };

  /**
   * Verifica se o usuário pode editar o relatório
   * - O relatório pode ser editado por usuários com perfil administrador
   * - O relatorio pode ser editado por usuarios que forem inspetor, auditor ou monitor
   */
  const canEditRelatorio = (estabelecimentoId: number) => {
    const isAdmin = usuarioLogado.perfil.id === 1;
    if (isAdmin) {
      return true;
    }

    const estabelecimento = listaEstabelecimentos.find(
      (estab) => estab.id === estabelecimentoId
    );

    if (!estabelecimento) {
      return false;
    }

    const pessoaInspecao = estabelecimento?.pessoasInspecao.find(
      (pi) => pi.id === usuarioLogado.pessoaId
    );

    if (!pessoaInspecao) {
      return false;
    }

    const userIsVeterinario =
      pessoaInspecao.funcao.id === ID_VETERINARIO_OFICIAL;
    const userIsInspetor = pessoaInspecao.inspetor;

    if (userIsVeterinario || userIsInspetor) {
      return true;
    }

    return false;
  };

  const syncRelatoriosFromApi = async () => {
    const relatoriosParaSincronizar = listaRelatorioAM.filter(
      (relatorio) =>
        relatorio.id !== undefined &&
        relatorio.id !== null &&
        relatorio.isUpdated
    );

    const promises = relatoriosParaSincronizar.map(async (relatorio) => {
      try {
        const relatorioAtualizado = await getRelatorioAMByIdApi(relatorio.id!);

        updateRelatorioAM({
          ...relatorioAtualizado,
          offlineId: relatorio.offlineId,
          isUpdated: true,
        });
        return { success: true, relatorioId: relatorio.id };
      } catch (error) {
        console.error(`Erro ao sincronizar relatório ${relatorio.id}:`, error);
        return { success: false, relatorioId: relatorio.id };
      }
    });

    const results = await Promise.allSettled(promises);
    return results;
  };

  return {
    listaRelatorioAM,
    addRelatorioAM,
    removeRelatorioAM,
    updateRelatorioAM,
    getRelatorioAMByOfflineId,
    submitRelatorioAMApi,
    cancelarRelatorioAMApi,
    getRelatorioAMByIdApi,
    getRelatoriosAMApi,
    getAnexoApi,
    updateRelatorioAMApi,
    validateFinalizadoRelatorio,
    submitPlanoAcaoRelatorioAMApi,
    getAnexoPlanoAcaoApi,
    getPlanoAcaoByItemIdApi,
    submitParecerApi,
    removerParecerPlanoAcaoApi,
    canMakePlanoAcao,
    canMakeParecer,
    canViewPlanoAcao,
    canEditRelatorio,
    canCancelRelatorio,
    canViewAndPrintRelatorio,
    syncRelatoriosFromApi,
  };
};
